import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../api';
import { HeadActions } from '../components/Layout';
import { IconCam, IconHealth, IconLive, IconTrend, IconUsers } from '../components/Icons';
import { useAuth } from '../auth';

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

export default function Home() {
  const ctx = useOutletContext();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .summary('daily', today())
      .then(setSummary)
      .catch((err) => setError(err.message));
  }, []);

  const modules = [
    {
      to: '/uptime',
      icon: <IconTrend />,
      title: 'CCTV Uptime',
      desc: 'Detailed reports by day, week, month, and year.',
    },
    {
      to: '/health',
      icon: <IconHealth />,
      title: 'CCTV Health',
      desc: 'Daily auto ticks at 9:00 AM and 4:00 PM.',
    },
    {
      to: '/live',
      icon: <IconLive />,
      title: 'Live View',
      desc: 'Camera preview and live video from DSS.',
    },
    {
      to: '/devices',
      icon: <IconCam />,
      title: 'Devices',
      desc: 'Camera inventory and connection status from DSS.',
    },
  ];

  if (user?.role === 'admin') {
    modules.push({
      to: '/users',
      icon: <IconUsers />,
      title: 'Users',
      desc: 'Manage AeroNMS operator and administrator accounts.',
    });
  }

  const kpis = [
    { key: 'online', label: 'ONLINE', value: summary?.online ?? '—', sub: 'Cameras online' },
    { key: 'offline', label: 'OFFLINE', value: summary?.offline ?? '—', sub: 'Cameras offline' },
    { key: 'unchecked', label: 'UNCHECKED', value: summary?.unchecked ?? '—', sub: 'Not checked yet' },
    {
      key: 'uptime',
      label: 'UPTIME',
      value: summary ? `${Number(summary.uptimePercent).toFixed(1)}%` : '—',
      sub: summary?.period?.modeLabel || 'Selected period',
      accent: true,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p>AeroNMS operations overview.</p>
        </div>
        <HeadActions {...ctx} />
      </div>

      {error && <p className="error">{error}</p>}

      <p className="period-line">
        {summary?.period
          ? `Active period: ${summary.period.label} | Mode: ${summary.period.modeLabel}`
          : 'Loading active period…'}
      </p>

      <div className="stat-row">
        {kpis.map((card) => (
          <Link key={card.key} to="/uptime" className={`stat-card${card.accent ? ' accent' : ''}`}>
            <div className="stat-kicker">{card.label}</div>
            <div className="stat-value">{card.value}</div>
            <div className="stat-sub">{card.sub}</div>
          </Link>
        ))}
      </div>

      <section className="panel">
        <div className="panel-head">
          <strong>Modules</strong>
        </div>
        <div className="module-grid">
          {modules.map((item) => (
            <Link key={item.to} to={item.to} className="module-card">
              <div className="module-icon">{item.icon}</div>
              <div>
                <h2>{item.title}</h2>
                <p>{item.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
